import { Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button as ShButton } from '../../components/shadcn/index.js';
import { platformApi } from '../../lib/platformApi.js';

const MAX_BYTES = 2 * 1024 * 1024;
// Mirrors the server's ALLOWED_LOGO_MIME (platform-uploads.controller.ts).
// SVG was dropped as a XSS-hardening measure; the file picker should not
// list it as accepted, otherwise the user picks one and gets a confusing
// "Use PNG, JPG, WebP, SVG, or GIF" error after the server rejects it.
const ALLOWED_MIME = new Set<string>([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

interface LogoUploadProps {
  /** Current logo URL (or null). When the upload completes, onChange fires
   *  with the new absolute URL the server hands back. */
  value: string | null;
  onChange: (next: string | null) => void;
  /** Optional override for the label shown above the dropzone. */
  label?: string;
}

/**
 * File-picker logo uploader. Posts the raw bytes to
 *   POST /platform/uploads/logo?mimeType=image/png
 * and propagates the returned absolute URL via onChange. Designed to
 * be drop-in compatible with the previous URL-input — same value /
 * onChange contract so the surrounding form code didn't change shape.
 */
export function LogoUpload({ value, onChange, label }: LogoUploadProps): JSX.Element {
  const { t } = useTranslation('platform');
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (file: File) => {
    setError(null);
    if (!ALLOWED_MIME.has(file.type)) {
      setError(t('logoUpload.errors.mime', {
        defaultValue: 'Use PNG, JPG, WebP, or GIF.',
      }));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('logoUpload.errors.size', {
        defaultValue: 'Logo must be 2 MB or smaller.',
      }));
      return;
    }
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      // platformApi already injects the bearer token + base URL.
      // Override the Content-Type because the default is JSON.
      const r = await platformApi().post<{ url: string }>(
        '/uploads/logo',
        buf,
        {
          params: { mimeType: file.type },
          headers: { 'Content-Type': 'application/octet-stream' },
          transformRequest: [(d) => d],
        },
      );
      onChange(r.data.url);
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } })
        .response?.data?.cause?.code;
      setError(
        code === 'logo_too_large'
          ? t('logoUpload.errors.size', { defaultValue: 'Logo must be 2 MB or smaller.' })
          : code === 'unsupported_logo_mime'
            ? t('logoUpload.errors.mime', { defaultValue: 'Use PNG, JPG, WebP, or GIF.' })
            : t('logoUpload.errors.generic', { defaultValue: 'Upload failed. Try again.' }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {label && (
        <label className="mb-1 block text-sm font-medium text-fg">{label}</label>
      )}
      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="h-14 w-14 rounded-md object-cover ring-1 ring-border"
            referrerPolicy="no-referrer"
            onError={() => setError(t('logoUpload.errors.broken', {
              defaultValue: "We saved the file, but the browser couldn't load it.",
            }))}
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-border bg-bg-subtle text-fg-muted">
            <ImageIcon className="h-6 w-6" aria-hidden />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept={[...ALLOWED_MIME].join(',')}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              // Reset so picking the same file twice re-fires onChange.
              if (e.target) e.target.value = '';
            }}
          />
          <div className="flex flex-wrap gap-2">
            <ShButton
              type="button"
              variant="outline"
              size="sm"
              onClick={onPick}
              disabled={busy}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {t('logoUpload.uploading', { defaultValue: 'Uploading…' })}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" aria-hidden />
                  {value
                    ? t('logoUpload.replace', { defaultValue: 'Replace logo' })
                    : t('logoUpload.pick', { defaultValue: 'Upload logo' })}
                </>
              )}
            </ShButton>
            {value && (
              <ShButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(null)}
                disabled={busy}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                {t('logoUpload.remove', { defaultValue: 'Remove' })}
              </ShButton>
            )}
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {t('logoUpload.hint', {
              defaultValue: 'PNG, JPG, WebP, or GIF — max 2 MB. Square renders best.',
            })}
          </p>
          {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>
      </div>
    </div>
  );
}
